import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import {
  createInventory,
  deleteInventory,
  getInventory,
  searchInventoryIdentityOptions,
  updateInventory,
} from "../api/inventory";
import PageHeader from "../components/PageHeader";
import CatalogFilters from "../components/CatalogFilters";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import { useConnectivity } from "../context/ConnectivityContext";
import { getOfflineInventoryPage } from "../offline/catalog";
import { useSearchParams } from "react-router-dom";
import useDebouncedValue from "../hooks/useDebouncedValue";
import MainLayout from "../layouts/MainLayout";
import { formatMoney, titleCase } from "../utils/format";
import { concreteIdentityKey, variantDisplayName } from "../utils/productVariants";

const PAGE_SIZE = 24;
const CACHE_TTL_MS = 20_000;
const MAX_CACHE_ENTRIES = 120;
const pageCache = new Map();
const emptyCreate = {
  quantity: "0",
  minimum_stock: "5",
  location: "",
};

function productOptionLabel(option) {
  if (!option || typeof option !== "object") return "";
  return [
    option.brand,
    option.product_name,
    option.variant_label ? `· ${option.variant_label}` : "",
    option.variant_sku ? `(${option.variant_sku})` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function cacheKey(userId, workspaceId, storeId, query, status, brand, category, page) {
  return `${userId || "anonymous"}|${workspaceId || "none"}|${storeId || "none"}|${query || ""}|${status || "all"}|${brand || ""}|${category || ""}|${page}|${PAGE_SIZE}`;
}

function invalidateStoreCache(userId, workspaceId, storeId) {
  const prefix = `${userId || "anonymous"}|${workspaceId || "none"}|${storeId || "none"}|`;
  for (const key of pageCache.keys()) {
    if (key.startsWith(prefix)) pageCache.delete(key);
  }
}

function cachePage(key, value) {
  pageCache.delete(key);
  pageCache.set(key, value);
  while (pageCache.size > MAX_CACHE_ENTRIES) {
    pageCache.delete(pageCache.keys().next().value);
  }
}

export default function Inventory() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lowStockOnly = searchParams.get("status") === "low-stock";
  const { canManage, canEdit, selectedWorkspace, selectedStore, user } = useAuth();
  const { isOnline } = useConnectivity();
  const offlineReadOnly = !isOnline;
  const canEditOnline = canEdit && isOnline;
  const canManageOnline = canManage && isOnline;
  const [inventory, setInventory] = useState([]);
  const [total, setTotal] = useState(0);
  const [lowStockTotal, setLowStockTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editForm, setEditForm] = useState({ quantity: "", minimum_stock: "", location: "", unit_acquisition_cost: "" });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [productOptionsLoading, setProductOptionsLoading] = useState(false);
  const inventoryRef = useRef(inventory);

  const debouncedQuery = useDebouncedValue(query.trim(), 350);
  const debouncedProductSearch = useDebouncedValue(productSearch.trim(), 300);

  useEffect(() => {
    inventoryRef.current = inventory;
  }, [inventory]);

  useEffect(() => {
    if (isOnline) return;
    setCreateOpen(false);
    setEditItem(null);
    setSelectedProduct(null);
  }, [isOnline]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, lowStockOnly, brandFilter, categoryFilter, selectedStore?.public_id, user?.public_id]);

  useEffect(() => { setBrandFilter(""); setCategoryFilter(""); }, [selectedWorkspace?.public_id]);

  const load = useCallback(async ({ force = false, signal } = {}) => {
    if (!isOnline) {
      if (inventoryRef.current.length) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const local = await getOfflineInventoryPage({
          workspacePublicId: selectedWorkspace?.public_id,
          storePublicId: selectedStore?.public_id,
          query: debouncedQuery,
          page,
          pageSize: PAGE_SIZE,
          status: lowStockOnly ? "low-stock" : null,
          brand: brandFilter,
          category: categoryFilter,
        });
        setInventory(local.items);
        setTotal(local.total);
        setLowStockTotal(local.lowStockTotal);
        setSnapshotMeta(local.metadata);
        if (!local.metadata) {
          setError(t("inventory.snapshotMissing"));
        }
      } catch (storageError) {
        setInventory([]);
        setTotal(0);
        setLowStockTotal(0);
        setSnapshotMeta(null);
        setError(storageError?.message || t("inventory.snapshotError"));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
      return;
    }

    setSnapshotMeta(null);
    const key = cacheKey(
      user?.public_id,
      selectedWorkspace?.public_id,
      selectedStore?.public_id,
      debouncedQuery,
      lowStockOnly ? "low-stock" : "all",
      brandFilter,
      categoryFilter,
      page,
    );
    const cached = pageCache.get(key);
    const isFresh = cached && Date.now() - cached.cachedAt < CACHE_TTL_MS;

    if (!force && isFresh) {
      setInventory(cached.items);
      setTotal(cached.total);
      setLowStockTotal(cached.lowStockTotal);
      setLoading(false);
      return;
    }

    if (inventoryRef.current.length) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const response = await getInventory(
        {
          query: debouncedQuery || undefined,
          page,
          page_size: PAGE_SIZE,
          status: lowStockOnly ? "low-stock" : undefined,
          brand: brandFilter || undefined,
          category: categoryFilter || undefined,
        },
        { signal },
      );
      const payload = response.data || {};
      const nextItems = Array.isArray(payload.items) ? payload.items : [];
      const nextTotal = Number(payload.total || 0);
      const nextLowStockTotal = Number(payload.low_stock_total || 0);

      setInventory(nextItems);
      setTotal(nextTotal);
      setLowStockTotal(nextLowStockTotal);
      cachePage(key, {
        items: nextItems,
        total: nextTotal,
        lowStockTotal: nextLowStockTotal,
        cachedAt: Date.now(),
      });
    } catch (requestError) {
      if (requestError?.code === "ERR_CANCELED") return;
      setError(getApiError(requestError, t("inventory.loadError")));
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [
    debouncedQuery,
    isOnline,
    lowStockOnly,
    brandFilter,
    categoryFilter,
    page,
    selectedStore?.public_id,
    selectedWorkspace?.public_id,
    user?.public_id,
    t,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!createOpen || !canManageOnline) return undefined;

    let active = true;
    const controller = new AbortController();
    setProductOptionsLoading(true);

    searchInventoryIdentityOptions(
      debouncedProductSearch,
      20,
      { signal: controller.signal },
    )
      .then((response) => {
        if (!active) return;
        setProductOptions(response.data?.items || []);
      })
      .catch((requestError) => {
        if (!active) return;
        if (requestError?.code === "ERR_CANCELED") return;
        setError(getApiError(requestError, t("inventory.searchError")));
        setProductOptions([]);
      })
      .finally(() => {
        if (active) setProductOptionsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [canManageOnline, createOpen, debouncedProductSearch, selectedStore?.public_id, t]);

  const openCreate = () => {
    setCreateForm({ ...emptyCreate, location: selectedStore?.name || "Main Store" });
    setSelectedProduct(null);
    setProductSearch("");
    setProductOptions([]);
    setError("");
    setCreateOpen(true);
  };

  const createRecord = async () => {
    if (!isOnline) {
      setError(t("inventory.locked"));
      return;
    }
    const quantity = Number(createForm.quantity);
    const minimumStock = Number(createForm.minimum_stock);
    if (!selectedProduct?.product_public_id) {
      setError(t("inventory.selectProduct"));
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 0 || !Number.isInteger(minimumStock) || minimumStock < 0) {
      setError(t("inventory.invalidQuantity"));
      return;
    }

    setSaving(true);
    setError("");
    try {
      await createInventory({
        product_public_id: selectedProduct.product_public_id,
        product_variant_public_id: selectedProduct.product_variant_public_id || null,
        store_public_id: selectedStore.public_id,
        quantity,
        minimum_stock: minimumStock,
        location: createForm.location.trim() || selectedStore.name,
        source: "manual",
      });
      setCreateOpen(false);
      setSuccess(t("inventory.created"));
      invalidateStoreCache(user?.public_id, selectedWorkspace?.public_id, selectedStore?.public_id);
      setPage(1);
      await load({ force: true });
    } catch (requestError) {
      setError(getApiError(requestError, t("inventory.createError")));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      quantity: String(item.quantity),
      minimum_stock: String(item.minimum_stock),
      location: item.location,
      unit_acquisition_cost: "",
    });
    setError("");
  };

  const saveEdit = async () => {
    if (!isOnline) {
      setError(t("inventory.locked"));
      return;
    }
    const quantity = Number(editForm.quantity);
    const minimumStock = Number(editForm.minimum_stock);
    if (!Number.isInteger(quantity) || quantity < 0 || !Number.isInteger(minimumStock) || minimumStock < 0) {
      setError(t("inventory.invalidQuantity"));
      return;
    }
    if (editItem && quantity < Number(editItem.reserved_quantity || 0)) {
      setError(t("inventory.reservedError"));
      return;
    }
    const increasingStock = editItem && quantity > Number(editItem.quantity);
    if (increasingStock && !canManage) {
      setError(t("inventory.managerCost"));
      return;
    }
    if (increasingStock && (editForm.unit_acquisition_cost === "" || Number(editForm.unit_acquisition_cost) < 0)) {
      setError(t("inventory.costRequired"));
      return;
    }

    setSaving(true);
    setError("");
    try {
      await updateInventory(editItem.public_id, {
        quantity,
        minimum_stock: minimumStock,
        location: editForm.location.trim() || selectedStore.name,
        source: "manual",
        ...(increasingStock ? { unit_acquisition_cost: editForm.unit_acquisition_cost } : {}),
      });
      setEditItem(null);
      setSuccess(t("inventory.updated"));
      invalidateStoreCache(user?.public_id, selectedWorkspace?.public_id, selectedStore?.public_id);
      await load({ force: true });
    } catch (requestError) {
      setError(getApiError(requestError, t("inventory.updateError")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!isOnline) {
      setError(t("inventory.locked"));
      return;
    }
    if (!window.confirm(t("inventory.deleteConfirm", { name: variantDisplayName(item.product.name, item.variant?.label) }))) return;
    setError("");
    try {
      await deleteInventory(item.public_id);
      invalidateStoreCache(user?.public_id, selectedWorkspace?.public_id, selectedStore?.public_id);
      setSuccess(t("inventory.deleted"));
      if (inventory.length === 1 && page > 1) setPage((current) => current - 1);
      else await load({ force: true });
    } catch (requestError) {
      setError(getApiError(requestError, t("inventory.deleteError")));
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <MainLayout>
      <PageHeader
        eyebrow={t("inventory.eyebrow")}
        title={t("nav.inventory")}
        description={t("inventory.subtitle", { store: selectedStore?.name || t("purchase.selectedStore") })}
        actions={(
          <>
            <Chip
              label={t("catalog.records", { count: total.toLocaleString("en-US") })}
              variant="outlined"
              sx={{ height: 42, borderColor: "#dbe3ef", bgcolor: "#fff", fontWeight: 850, px: 0.5 }}
            />
            <Chip
              label={t("catalog.lowStockCount", { count: lowStockTotal.toLocaleString("en-US") })}
              color={lowStockTotal ? "warning" : "default"}
              variant={lowStockTotal ? "filled" : "outlined"}
              sx={{ height: 42, fontWeight: 850, px: 0.5 }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => load({ force: true })}
              disabled={refreshing}
              sx={{
                borderColor: "#cbd5e1",
                color: "text.primary",
                bgcolor: "#fff",
                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
              }}
            >
              {t(refreshing ? "catalog.refreshing" : "catalog.refresh")}
            </Button>
            {canManageOnline && (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ px: 2.25 }}>
                {t("inventory.add")}
              </Button>
            )}
          </>
        )}
      />

      {lowStockOnly && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button color="inherit" onClick={() => setSearchParams({})}>{t("inventory.clearFilter")}</Button>
        }>
          {t("inventory.lowFilter")}
        </Alert>
      )}

      {offlineReadOnly && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("inventory.offlineNotice", { timestamp: snapshotMeta?.cached_at ? ` (${new Date(snapshotMeta.cached_at).toLocaleString()})` : "" })}
        </Alert>
      )}
      {!offlineReadOnly && !canEdit && <Alert severity="info" sx={{ mb: 3 }}>{t("inventory.readOnly")}</Alert>}
      {!offlineReadOnly && canEdit && !canManage && <Alert severity="info" sx={{ mb: 3 }}>{t("inventory.staffHelp")}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Box
        sx={{
          mb: 2.5,
          p: { xs: 1.5, sm: 2 },
          border: "1px solid #e2e8f0",
          borderRadius: 4,
          bgcolor: "#fff",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.035)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ sm: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("inventory.search")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 800 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: { sm: "nowrap" } }}>
            {refreshing ? t("inventory.updating") : t("catalog.shown", { count: Math.min(inventory.length, PAGE_SIZE) })}
          </Typography>
        </Stack>
        <CatalogFilters brand={brandFilter} category={categoryFilter} onBrandChange={setBrandFilter}
          onCategoryChange={setCategoryFilter} isOnline={isOnline}
          workspacePublicId={selectedWorkspace?.public_id} storePublicId={selectedStore?.public_id} />
      </Box>

      {loading ? (
        <LoadingPanel label={t("inventory.loading")} />
      ) : inventory.length === 0 ? (
        <EmptyPanel
          icon="🏬"
          title={t(lowStockOnly ? "inventory.noLow" : debouncedQuery ? "inventory.noMatch" : "inventory.empty")}
          description={t(debouncedQuery ? "inventory.noMatchHelp" : "inventory.emptyHelp")}
        />
      ) : (
        <>
          <TableContainer
            sx={{
              display: { xs: "none", lg: "block" },
              bgcolor: "#fff",
              borderRadius: 4,
              border: "1px solid #e2e8f0",
              boxShadow: "0 14px 34px rgba(15, 23, 42, 0.045)",
              overflow: "hidden",
            }}
          >
            <Table sx={{ minWidth: 1120 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 2.5 }}>{t("common.product")}</TableCell>
                  <TableCell>{t("common.status")}</TableCell>
                  <TableCell align="right">{t("catalog.available")}</TableCell>
                  <TableCell align="right">{t("catalog.reserved")}</TableCell>
                  <TableCell align="right">{t("catalog.physical")}</TableCell>
                  <TableCell align="right">{t("catalog.minimum")}</TableCell>
                  {canManageOnline && <TableCell align="right">{t("catalog.averageCost")}</TableCell>}
                  <TableCell>{t("catalog.location")}</TableCell>
                  <TableCell>{t("catalog.source")}</TableCell>
                  {canEditOnline && <TableCell align="right" sx={{ pr: 2.5 }}>{t("common.actions")}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory.map((item, index) => {
                  const availableQuantity = item.available_quantity ?? Math.max(0, Number(item.quantity || 0) - Number(item.reserved_quantity || 0));
                  const startsVariantGroup = Boolean(
                    item.variant
                    && inventory[index - 1]?.product?.public_id !== item.product.public_id,
                  );
                  return (
                    <Fragment key={item.public_id}>
                    {startsVariantGroup && (
                      <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                        <TableCell colSpan={canEditOnline ? (canManageOnline ? 10 : 9) : 8} sx={{ py: 1, pl: 2.5, borderBottomColor: "#cbd5e1" }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography fontWeight={950}>{item.product.name}</Typography>
                            <Chip label={t("inventory.variantInventory")} size="small" color="secondary" variant="outlined" />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      hover
                      sx={{
                        bgcolor: item.is_low_stock ? "#fffdf5" : "transparent",
                        "&:hover": { bgcolor: item.is_low_stock ? "#fff8e7" : "#f8fbff" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ py: 1.6, pl: 2.5, minWidth: 280 }}>
                        <Stack direction="row" spacing={1.4} alignItems="center">
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              flex: "0 0 auto",
                              borderRadius: 2.5,
                              display: "grid",
                              placeItems: "center",
                              color: item.is_low_stock ? "warning.dark" : "primary.main",
                              bgcolor: item.is_low_stock ? "#fff7ed" : "#eff6ff",
                              border: item.is_low_stock ? "1px solid #fed7aa" : "1px solid #dbeafe",
                            }}
                          >
                            <WarehouseRoundedIcon fontSize="small" />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={850} sx={{ lineHeight: 1.3 }}>{item.product.name}</Typography>
                            {item.variant && <Typography variant="body2" color="primary.main" fontWeight={800}>{item.variant.label}{item.variant.sku ? ` · ${item.variant.sku}` : ""}</Typography>}
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3 }}>
                              {item.product.brand || item.store.name}{item.product.external_id ? ` · ID ${item.product.external_id}` : ""}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={t(item.is_low_stock ? "catalog.lowStock" : "catalog.healthy")} color={item.is_low_stock ? "warning" : "success"} size="small" sx={{ fontWeight: 850 }} />
                      </TableCell>
                      <TableCell align="right"><Typography fontWeight={900} sx={{ fontVariantNumeric: "tabular-nums" }}>{availableQuantity}</Typography></TableCell>
                      <TableCell align="right"><Typography color="text.secondary" fontWeight={750} sx={{ fontVariantNumeric: "tabular-nums" }}>{item.reserved_quantity || 0}</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight={800} sx={{ fontVariantNumeric: "tabular-nums" }}>{item.quantity}</Typography></TableCell>
                      <TableCell align="right"><Typography color="text.secondary" fontWeight={750} sx={{ fontVariantNumeric: "tabular-nums" }}>{item.minimum_stock}</Typography></TableCell>
                      {canManageOnline && <TableCell align="right"><Typography variant="body2" fontWeight={750}>{item.average_unit_cost == null ? t("catalog.costUnknown") : formatMoney(item.average_unit_cost)}</Typography></TableCell>}
                      <TableCell>
                        <Typography variant="body2" fontWeight={750}>{item.location || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.store.name}</Typography>
                      </TableCell>
                      <TableCell><Chip label={titleCase(item.source)} size="small" variant="outlined" /></TableCell>
                      {canEditOnline && (
                        <TableCell align="right" sx={{ pr: 2.5, whiteSpace: "nowrap" }}>
                          <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                            <Button size="small" variant="outlined" startIcon={<EditRoundedIcon fontSize="small" />} onClick={() => openEdit(item)} sx={{ minHeight: 34, px: 1.25, borderColor: "#cbd5e1", color: "text.primary" }}>
                              {t("common.edit")}
                            </Button>
                            {canManageOnline && (
                              <Button size="small" color="error" variant="outlined" startIcon={<DeleteOutlineRoundedIcon fontSize="small" />} onClick={() => remove(item)} sx={{ minHeight: 34, px: 1.25 }}>
                                {t("common.delete")}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={1.75} sx={{ display: { xs: "flex", lg: "none" } }}>
            {inventory.map((item) => {
              const availableQuantity = item.available_quantity ?? Math.max(0, Number(item.quantity || 0) - Number(item.reserved_quantity || 0));
              return (
                <Grid item xs={12} sm={6} key={item.public_id}>
                  <Card sx={{ height: "100%", borderRadius: 3.5, borderColor: item.is_low_stock ? "#f59e0b" : "#e2e8f0", boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)" }}>
                    <CardContent sx={{ p: 2.25 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.25}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.25 }}>{item.product.name}</Typography>
                          {item.variant && <Typography variant="body2" color="primary.main" fontWeight={800}>{item.variant.label}{item.variant.sku ? ` · ${item.variant.sku}` : ""}</Typography>}
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>{item.location || item.store.name}</Typography>
                        </Box>
                        <Chip label={t(item.is_low_stock ? "catalog.lowStock" : "catalog.healthy")} color={item.is_low_stock ? "warning" : "success"} size="small" />
                      </Stack>
                      <Grid container spacing={1} sx={{ mt: 1.25 }}>
                        {[
                          [t("catalog.available"), availableQuantity],
                          [t("catalog.reserved"), item.reserved_quantity || 0],
                          [t("catalog.physical"), item.quantity],
                          [t("catalog.minimum"), item.minimum_stock],
                          ...(canManageOnline ? [[t("catalog.averageCost"), item.average_unit_cost == null ? t("catalog.costUnknown") : formatMoney(item.average_unit_cost)]] : []),
                        ].map(([label, value]) => (
                          <Grid item xs={6} key={label}>
                            <Box sx={{ p: 1.25, bgcolor: "#f8fafc", border: "1px solid #edf2f7", borderRadius: 2.5 }}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="h6" fontWeight={900} sx={{ mt: 0.15, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                      {canEditOnline && (
                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          <Button fullWidth variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => openEdit(item)}>{t("common.edit")}</Button>
                          {canManageOnline && <Button fullWidth variant="outlined" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => remove(item)}>{t("common.delete")}</Button>}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {pageCount > 1 && (
            <Stack alignItems="center" sx={{ mt: 4 }} spacing={1}>
              <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" />
              <Typography variant="caption" color="text.secondary">
                {t("catalog.showing", { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total: total.toLocaleString("en-US") })}
              </Typography>
            </Stack>
          )}
        </>
      )}

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={900}>{t("inventory.add")}</DialogTitle>
        <DialogContent>
          <Autocomplete
            value={selectedProduct}
            onChange={(_, value) => {
              setSelectedProduct(value);
              setProductSearch(productOptionLabel(value));
            }}
            inputValue={productSearch}
            onInputChange={(_, value, reason) => {
              if (reason === "input") {
                if (
                  selectedProduct
                  && value !== productOptionLabel(selectedProduct)
                ) {
                  setSelectedProduct(null);
                }
                setProductSearch(value);
                return;
              }

              if (reason === "clear") {
                setSelectedProduct(null);
                setProductSearch("");
                return;
              }

              if (reason === "reset") setProductSearch(value);
            }}
            options={
              selectedProduct
              && !productOptions.some(
                (option) => concreteIdentityKey(option.product_public_id, option.product_variant_public_id) === concreteIdentityKey(selectedProduct.product_public_id, selectedProduct.product_variant_public_id),
              )
                ? [selectedProduct, ...productOptions]
                : productOptions
            }
            loading={productOptionsLoading}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, value) => concreteIdentityKey(option.product_public_id, option.product_variant_public_id) === concreteIdentityKey(value.product_public_id, value.product_variant_public_id)}
            getOptionLabel={productOptionLabel}
            noOptionsText={t(productSearch ? "inventory.noIdentityMatch" : "inventory.noIdentities")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("inventory.selectIdentity")}
                margin="normal"
                helperText={t("inventory.identityHelp")}
                InputProps={{
                  ...(params.InputProps || {}),
                  endAdornment: (
                    <>
                      {productOptionsLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps?.endAdornment || null}
                    </>
                  ),
                }}
              />
            )}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label={t("common.quantity")} type="number" inputProps={{ min: 0, step: 1 }} value={createForm.quantity} onChange={(event) => setCreateForm((current) => ({ ...current, quantity: event.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth label={t("catalog.minimumStock")} type="number" inputProps={{ min: 0, step: 1 }} value={createForm.minimum_stock} onChange={(event) => setCreateForm((current) => ({ ...current, minimum_stock: event.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth label={t("catalog.location")} value={createForm.location} onChange={(event) => setCreateForm((current) => ({ ...current, location: event.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setCreateOpen(false)} disabled={saving}>{t("common.cancel")}</Button><Button variant="contained" onClick={createRecord} disabled={saving}>{saving ? <CircularProgress size={21} color="inherit" /> : t("catalog.create")}</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(editItem)} onClose={() => !saving && setEditItem(null)} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={900}>{t("inventory.edit", { name: variantDisplayName(editItem?.product?.name, editItem?.variant?.label) })}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={6}><TextField fullWidth label={t("inventory.physicalQuantity")} type="number" inputProps={{ min: Number(editItem?.reserved_quantity || 0), step: 1 }} value={editForm.quantity} onChange={(event) => setEditForm((current) => ({ ...current, quantity: event.target.value }))} helperText={t("inventory.reservedHelp", { count: Number(editItem?.reserved_quantity || 0) })} /></Grid>
            {Number(editForm.quantity) > Number(editItem?.quantity || 0) && canManage && <Grid item xs={12}><TextField fullWidth required label={t("inventory.acquisitionCost")} type="number" inputProps={{ min: 0, step: "0.01" }} value={editForm.unit_acquisition_cost} onChange={(event) => setEditForm((current) => ({ ...current, unit_acquisition_cost: event.target.value }))} helperText={t("inventory.acquisitionHelp")} /></Grid>}
            <Grid item xs={6}><TextField fullWidth label={t("catalog.minimumStock")} type="number" inputProps={{ min: 0, step: 1 }} value={editForm.minimum_stock} onChange={(event) => setEditForm((current) => ({ ...current, minimum_stock: event.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth label={t("catalog.location")} value={editForm.location} onChange={(event) => setEditForm((current) => ({ ...current, location: event.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setEditItem(null)} disabled={saving}>{t("common.cancel")}</Button><Button variant="contained" onClick={saveEdit} disabled={saving}>{saving ? <CircularProgress size={21} color="inherit" /> : t("common.save")}</Button></DialogActions>
      </Dialog>
    </MainLayout>
  );
}
